import { createClient, type Entry, type ContentfulClientApi } from 'contentful';
import type { NewsItem, Event, Contact, Resource, PaymentSchedule, PageItem, RoadmapItem, AgencyContent } from '../types/contentful';
import { fetchV2WithAuth } from './apiAuth';
import { toLocalDateString } from '../utils/eventDate';

// Lazy client init: createClient throws when accessToken is missing, so we
// defer construction until the first call. This keeps tests / preview builds
// without Contentful credentials from crashing at module load.
let _client: ContentfulClientApi<undefined> | null = null;
function getClient(): ContentfulClientApi<undefined> {
  if (_client) return _client;
  const space = import.meta.env.VITE_CONTENTFUL_SPACE_ID;
  const accessToken = import.meta.env.VITE_CONTENTFUL_ACCESS_TOKEN;
  if (!space || !accessToken) {
    throw new Error('Contentful is not configured (missing VITE_CONTENTFUL_SPACE_ID or VITE_CONTENTFUL_ACCESS_TOKEN)');
  }
  _client = createClient({
    space,
    accessToken,
    environment: import.meta.env.VITE_CONTENTFUL_ENVIRONMENT || 'master',
  });
  return _client;
}

// Backward-compatible proxy: any property access (e.g. `client.getEntries(...)`)
// triggers lazy init. Tests that mock this module won't trigger it at all.
const client = new Proxy({} as ContentfulClientApi<undefined>, {
  get(_target, prop) {
    return (getClient() as unknown as Record<string | symbol, unknown>)[prop];
  },
});

// Helper function to parse Contentful entries
function parseEntry<T>(entry: Entry): T {
  return {
    id: entry.sys.id,
    ...entry.fields,
  } as T;
}

function extractPdfUrl(fields: any): string | undefined {
  // Check dedicated pdfUrl asset field
  if (fields.pdfUrl && fields.pdfUrl.fields?.file?.url) {
    const url = fields.pdfUrl.fields.file.url;
    return url.startsWith('//') ? `https:${url}` : url;
  }

  // Check for PDF embedded in rich text content
  if (fields.content && typeof fields.content === 'object' && fields.content.content) {
    for (const node of fields.content.content) {
      if (node.nodeType === 'embedded-asset-block') {
        const file = node.data?.target?.fields?.file;
        if (file && file.contentType === 'application/pdf') {
          return file.url.startsWith('//') ? `https:${file.url}` : file.url;
        }
      }
    }
  }

  return undefined;
}

function parseNewsEntry(entry: Entry): NewsItem {
  const fields = entry.fields as any;
  const pdfUrl = extractPdfUrl(fields);

  let imageUrl: string | undefined;
  const imageAsset = fields.image || fields.photo || fields.featuredImage;
  if (imageAsset && imageAsset.fields?.file?.url) {
    const url = imageAsset.fields.file.url;
    imageUrl = url.startsWith('//') ? `https:${url}` : url;
  }
  if (!imageUrl && fields.content && typeof fields.content === 'object' && fields.content.content) {
    for (const node of fields.content.content) {
      if (node.nodeType === 'embedded-asset-block') {
        const file = node.data?.target?.fields?.file;
        if (file && file.contentType?.startsWith('image/')) {
          imageUrl = file.url.startsWith('//') ? `https:${file.url}` : file.url;
          break;
        }
      }
    }
  }

  return {
    id: entry.sys.id,
    title: fields.title,
    content: fields.content,
    date: fields.date,
    featured: fields.featured,
    renderPdf: fields.renderPdf,
    pdfUrl,
    renderImage: fields.renderImage,
    imageUrl,
    insuranceNews: fields.insuranceNews,
    articleUrl: fields.articleUrl,
    articleHeadline: fields.articleHeadline,
  };
}

// Fetch news items
export async function getNewsItems(limit = 10, skipDateFilter = false): Promise<NewsItem[]> {
  try {
    const query: any = {
      content_type: 'newsItem',
      limit,
      order: ['-fields.date'],
      include: 2,
    };

    if (!skipDateFilter) {
      // Show the previous month through any future date. A cutoff of "today
      // onwards" dropped a post the day after its date, so recent
      // announcements — including featured ones — vanished while still current.
      // Filtering in the query rather than on the response means `limit` bounds
      // the items actually shown; filtering afterwards let a stale entry consume
      // a slot and push a current one off the dashboard entirely.
      const cutoff = new Date();
      cutoff.setMonth(cutoff.getMonth() - 1);
      query['fields.date[gte]'] = toLocalDateString(cutoff);
    }

    const response = await client.getEntries(query);

    return response.items.map((item) => parseNewsEntry(item));
  } catch (error) {
    console.error('Error fetching news items:', error);
    throw error;
  }
}

// Fetch every published news item, for the archive view. Deliberately has no
// date filter: getNewsItems() limits the dashboard to a recent window, but the
// archive needs the older items that window excludes, plus undated ones.
// Pages through the Delivery API, which caps a single response at 1000 entries.
export async function getAllNewsItems(): Promise<NewsItem[]> {
  const PAGE_SIZE = 200;
  try {
    const items: Entry[] = [];
    let skip = 0;
    let total = Infinity;

    while (items.length < total) {
      const response = await client.getEntries({
        content_type: 'newsItem',
        limit: PAGE_SIZE,
        skip,
        // Undated entries sort last under -fields.date, so secondary-sort by
        // creation to keep them in a stable, roughly chronological order.
        order: ['-fields.date', '-sys.createdAt'],
        include: 2,
      });

      total = response.total;
      if (response.items.length === 0) break; // guard against a non-advancing page
      items.push(...response.items);
      skip += PAGE_SIZE;
    }

    return items.map((item) => parseNewsEntry(item));
  } catch (error) {
    console.error('Error fetching all news items:', error);
    throw error;
  }
}

// Fetch insurance news items (insuranceNews = true)
export async function getInsuranceNews(limit = 6): Promise<NewsItem[]> {
  try {
    const response = await client.getEntries({
      content_type: 'newsItem',
      'fields.insuranceNews': true,
      limit,
      order: ['-fields.date'],
      include: 2,
    });
    return response.items.map((item) => parseNewsEntry(item));
  } catch (error) {
    console.error('Error fetching insurance news:', error);
    return [];
  }
}

// Fetch upcoming events
export async function getUpcomingEvents(limit = 10): Promise<Event[]> {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Local day, not the UTC one, which is already tomorrow late in the evening
    // and would drop events happening today.
    const todayISO = toLocalDateString(today);

    // Fetch events with date >= today, plus undated events
    const [datedResponse, undatedResponse] = await Promise.all([
      client.getEntries({
        content_type: 'event',
        'fields.date[gte]': todayISO,
        order: ['fields.date'],
        limit,
      }),
      client.getEntries({
        content_type: 'event',
        'fields.date[exists]': false,
        limit,
      }),
    ]);

    const datedEvents = datedResponse.items.map((item) => parseEntry<Event>(item));
    const undatedEvents = undatedResponse.items.map((item) => parseEntry<Event>(item));

    return [...datedEvents, ...undatedEvents].slice(0, limit);
  } catch (error) {
    console.error('Error fetching events:', error);
    throw error;
  }
}

// Fetch events for a date range. Contentful holds one explicit entry per
// occurrence (editors maintain a separate entry for each month), so we simply
// return the entries whose date falls in the range — no client-side recurrence
// expansion, which previously double-booked months (WIPE-686).
export async function getEventsForDateRange(startDate: string, endDate: string): Promise<Event[]> {
  try {
    // Paged, because a single page stops at 100 and a busy month of carrier webinars is
    // already half that; a wider range (the Outlook repair's) runs past it.
    const events: Event[] = [];
    for (let skip = 0; ; ) {
      const rangeResponse = await client.getEntries({
        content_type: 'event',
        'fields.date[gte]': startDate,
        'fields.date[lte]': endDate,
        order: ['fields.date', 'sys.id'],
        limit: 100,
        skip,
      });
      events.push(...rangeResponse.items.map((item) => parseEntry<Event>(item)));
      skip += rangeResponse.items.length;
      if (rangeResponse.items.length === 0 || skip >= rangeResponse.total) return events;
    }
  } catch (error) {
    console.error('Error fetching events for date range:', error);
    throw error;
  }
}

// Fetch news items by carrier ID
export async function getNewsItemsByCarrierId(carrierId: string, limit = 10): Promise<NewsItem[]> {
  try {
    const response = await client.getEntries({
      content_type: 'newsItem',
      'fields.carrierId': carrierId,
      limit,
      include: 2,
      order: ['-fields.date'],
    });

    return response.items.map((item) => parseNewsEntry(item));
  } catch (error) {
    console.error('Error fetching news items by carrier ID:', error);
    throw error;
  }
}

// Fetch events by carrier ID
export async function getEventsByCarrierId(carrierId: string, limit = 10): Promise<Event[]> {
  try {
    const response = await client.getEntries({
      content_type: 'event',
      'fields.carrierId': carrierId,
      limit,
      order: ['fields.date'],
    });

    return response.items.map((item) => parseEntry<Event>(item));
  } catch (error) {
    console.error('Error fetching events by carrier ID:', error);
    throw error;
  }
}

// Fetch key contacts
export async function getKeyContacts(): Promise<Contact[]> {
  try {
    const response = await client.getEntries({
      content_type: 'contact',
      order: ['fields.displayOrder', 'fields.name'],
    });

    return response.items.map((item) => parseEntry<Contact>(item));
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error;
  }
}

// Fetch marketing resources
export async function getMarketingResources(): Promise<Resource[]> {
  try {
    const response = await client.getEntries({
      content_type: 'marketingResource',
      order: ['fields.category', 'fields.title'],
    });

    return response.items.map((item) => parseEntry<Resource>(item));
  } catch (error) {
    console.error('Error fetching marketing resources:', error);
    throw error;
  }
}

// Fetch payment schedules
export async function getPaymentSchedules(): Promise<PaymentSchedule[]> {
  try {
    const response = await client.getEntries({
      content_type: 'paymentSchedule',
      order: ['fields.paymentType'],
    });

    return response.items.map((item) => parseEntry<PaymentSchedule>(item));
  } catch (error) {
    console.error('Error fetching payment schedules:', error);
    throw error;
  }
}

// Fetch page item by slug
export async function getPageItemBySlug(slug: string): Promise<PageItem | null> {
  try {
    const response = await client.getEntries({
      content_type: 'pageItem',
      'fields.slug': slug,
      limit: 1,
    });

    if (response.items.length === 0) {
      return null;
    }

    return parseEntry<PageItem>(response.items[0]);
  } catch (error) {
    console.error('Error fetching page item:', error);
    throw error;
  }
}

// Fetch roadmap items
export async function getRoadmapItems(): Promise<RoadmapItem[]> {
  try {
    const response = await client.getEntries({
      content_type: 'roadmapItem',
      order: ['fields.sortOrder'],
    });

    return response.items.map((item) => parseEntry<RoadmapItem>(item));
  } catch (error) {
    console.error('Error fetching roadmap items:', error);
    throw error;
  }
}

// Search content across all types
export async function searchContent(query: string): Promise<Entry[]> {
  try {
    const response = await client.getEntries({
      query,
    });

    return response.items;
  } catch (error) {
    console.error('Error searching content:', error);
    throw error;
  }
}

// Get single entry by ID
export async function getEntryById<T>(id: string): Promise<T | null> {
  try {
    const entry = await client.getEntry(id);
    return parseEntry<T>(entry);
  } catch (error) {
    console.error('Error fetching entry:', error);
    throw error;
  }
}

// Extract plain text from a Contentful rich text document
function extractPlainText(doc: any): string {
  if (!doc || !doc.content) return '';
  return doc.content
    .map((node: any) => {
      if (node.content) {
        return node.content
          .filter((child: any) => child.nodeType === 'text')
          .map((child: any) => child.value || '')
          .join('');
      }
      return '';
    })
    .filter(Boolean)
    .join('\n');
}

// Fetch agency content by branch code
export async function getAgencyContentByBranch(branchCode: number): Promise<AgencyContent | null> {
  try {
    const response = await client.getEntries({
      content_type: 'agencyContent',
      'fields.branchCode': String(branchCode),
      limit: 1,
    });

    if (response.items.length === 0) {
      return null;
    }

    const entry = response.items[0];
    const fields = entry.fields as any;
    return {
      id: entry.sys.id,
      branchCode: parseInt(String(fields.branchCode).replace(/\D/g, ''), 10) || 0,
      agencyBio: extractPlainText(fields.agencyBio),
    };
  } catch (error) {
    console.error('Error fetching agency content:', error);
    return null;
  }
}

// Save an agency's public bio. Writes now go through the authenticated FranchiseApi backend
// (PUT /v2/branches/:id/bio), which holds the Contentful management token server-side and
// authorizes the caller per-branch. The browser no longer carries a management token.
export async function saveAgencyBio(branchId: string, bio: string): Promise<void> {
  const baseUrl = import.meta.env.VITE_FRANCHISE_API_URL;
  if (!baseUrl) throw new Error('Franchise API not configured');

  const response = await fetchV2WithAuth(`${baseUrl}/v2/branches/${encodeURIComponent(branchId)}/bio`, {
    method: 'PUT',
    body: JSON.stringify({ bio }),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to save bio: ${response.status}. ${text}`);
  }
}

export default client;
